import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import moment from 'moment-timezone';

interface ShareReportDialogProps {
  open: boolean;
  onClose: () => void;
  reportType: string;
  reportFilters: {
    startDate?: string;
    endDate?: string;
    [key: string]: any;
  };
  reportData?: any;
  reportTitle: string;
  onShareSuccess?: () => void;
}

const ShareReportDialog: React.FC<ShareReportDialogProps> = ({
  open,
  onClose,
  reportType,
  reportFilters,
  reportData,
  reportTitle,
  onShareSuccess,
}) => {
  const { user } = useAuth();
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachmentType, setAttachmentType] = useState<'pdf' | 'excel' | 'csv' | 'link'>('pdf');
  const [sendCopyToMe, setSendCopyToMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSharingEnabled, setIsSharingEnabled] = useState(true);

  // Check if report sharing is enabled
  useEffect(() => {
    const checkSharingEnabled = async () => {
      try {
        const response = await apiClient.globalSettings.get('report_sharing_enabled');
        // Response structure: { data: { success: boolean, data: { value: any, description: string } } }
        const value = response.data?.data?.value;
        setIsSharingEnabled(value === true || String(value) === 'true');
      } catch (error) {
        console.error('Error checking report sharing setting:', error);
        setIsSharingEnabled(false);
      }
    };
    if (open) {
      checkSharingEnabled();
      // Auto-generate subject
      const dateRange = reportFilters.startDate && reportFilters.endDate
        ? `${moment(reportFilters.startDate).format('MMM D, YYYY')} - ${moment(reportFilters.endDate).format('MMM D, YYYY')}`
        : moment().format('MMM D, YYYY');
      setSubject(`${reportTitle} - ${dateRange}`);
    }
  }, [open, reportFilters, reportTitle]);

  const handleClose = () => {
    if (!loading) {
      setRecipients('');
      setMessage('');
      setError(null);
      onClose();
    }
  };

  const handleShare = async () => {
    setError(null);
    setLoading(true);

    try {
      // Validate recipients
      if (!recipients.trim()) {
        setError('Please enter at least one recipient email address');
        setLoading(false);
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const recipientList = recipients.split(',').map(email => email.trim()).filter(Boolean);
      const invalidEmails = recipientList.filter(email => !emailRegex.test(email));
      
      if (invalidEmails.length > 0) {
        setError(`Invalid email addresses: ${invalidEmails.join(', ')}`);
        setLoading(false);
        return;
      }

      // Add sender to recipients if "Send Copy to Me" is checked
      const finalRecipients = sendCopyToMe && user?.email
        ? [...recipientList, user.email].join(', ')
        : recipientList.join(', ');

      // For now, we'll let the backend generate the file. If the caller
      // provides reportData (e.g., Scheduled Employees with incident details),
      // pass it through so the backend can export the exact rows/columns.

      const cleanedFilters = { ...reportFilters };
      delete cleanedFilters.page;
      delete cleanedFilters.limit;

      await apiClient.reportShares.share({
        reportType,
        reportFilters: cleanedFilters,
        recipients: finalRecipients,
        subject,
        message: message || undefined,
        attachmentType,
        reportData: reportData ?? undefined,
        reportTitle,
      });

      if (onShareSuccess) {
        onShareSuccess();
      }

      handleClose();
    } catch (error: any) {
      console.error('Error sharing report:', error);
      setError(error.response?.data?.message || 'Failed to share report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isSharingEnabled) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Report Sharing Disabled</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Report sharing is currently disabled. Please contact your administrator to enable this feature.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon color="primary" />
          <Typography variant="h6">Share Report via Email</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="To"
            fullWidth
            required
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            placeholder="email1@example.com, email2@example.com"
            helperText="Enter one or more email addresses, separated by commas"
            disabled={loading}
          />

          <TextField
            label="Subject"
            fullWidth
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={loading}
          />

          <TextField
            label="Message (Optional)"
            fullWidth
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Add a personal message to accompany the report..."
            disabled={loading}
          />

          <FormControl fullWidth>
            <InputLabel>Attach As</InputLabel>
            <Select
              value={attachmentType}
              onChange={(e) => setAttachmentType(e.target.value as 'pdf' | 'excel' | 'csv' | 'link')}
              label="Attach As"
              disabled={loading}
            >
              <MenuItem value="pdf">PDF</MenuItem>
              <MenuItem value="excel">Excel (XLSX)</MenuItem>
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="link">Link Only</MenuItem>
            </Select>
          </FormControl>

          {attachmentType === 'link' && (
            <Alert severity="info">
              A secure link to view the report in the app will be included in the email.
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={sendCopyToMe}
                onChange={(e) => setSendCopyToMe(e.target.checked)}
                disabled={loading}
              />
            }
            label="Send a copy to me"
          />

          <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Report Details:
            </Typography>
            <Chip label={reportTitle} size="small" sx={{ mr: 1, mb: 1 }} />
            {reportFilters.startDate && reportFilters.endDate && (
              <Chip
                label={`${moment(reportFilters.startDate).format('MMM D')} - ${moment(reportFilters.endDate).format('MMM D, YYYY')}`}
                size="small"
                sx={{ mr: 1 }}
              />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading} startIcon={<CloseIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleShare}
          variant="contained"
          disabled={loading || !recipients.trim() || !subject.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {loading ? 'Sending...' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareReportDialog;

